import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useImperativeHandle,
} from 'react';
import { useLocale } from '@adsk/alloy-react-helpers';
import PropTypes from 'prop-types';
import {
  useGetAccountCompanies,
  useAnalytics,
  useGetAccount,
} from '@common/hooks';
import {
  OP_STATUSES,
  ACCESS_LEVELS,
  MEMBER_STATUS,
  TRACK_EVENTS,
} from '@common/constants';
import { useGetAccountRoles } from './hooks';
import { ACTIONS } from './state/actions';
import { reducer, initialState } from './state/reducer';
import AccountMemberFilterPanel from './component';

const AccountMemberFilterPanelContainer = React.forwardRef(
  ({ onClose, open, onFilterUpdate }, ref) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const [state, dispatch] = useReducer(reducer, initialState);
    const { trackEvent } = useAnalytics();
    const [accessLevels, setAccessLevels] = useState([]);
    const [status, setStatus] = useState([]);
    const { accountData } = useGetAccount();
    const account = accountData || {};
    const isAccountAdmin = account.accessLevels?.accountAdmin ?? false;

    const sendAnalytics = useCallback(
      (type, data = {}) => {
        if (isAccountAdmin) {
          trackEvent(
            `${TRACK_EVENTS.SCOPE.ACCOUNT_ADMIN}.${TRACK_EVENTS.PAGE.MEMBERS}.${TRACK_EVENTS.SOURCE.FILTER_DRAWER}.${type}`,
            data
          );
        }
      },
      [isAccountAdmin, trackEvent]
    );

    const handleClose = () => {
      sendAnalytics(TRACK_EVENTS.NAME.CLOSE);
      if (onClose) onClose();
    };

    useEffect(() => {
      if (open) sendAnalytics(TRACK_EVENTS.NAME.OPEN);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const ACCESSES = useMemo(
      () => ({
        accountAdmin: {
          name: T.translate('ACCOUNT_ADMIN'),
          value: ACCESS_LEVELS.ACCOUNT_ADMIN,
        },
        executive: {
          name: T.translate('EXECUTIVE'),
          value: ACCESS_LEVELS.EXECUTIVE,
        },
        projectAdmin: {
          name: T.translate('PROJECT_ADMIN'),
          value: ACCESS_LEVELS.PROJECT_ADMIN,
        },
        projectMember: {
          name: T.translate('PROJECT_MEMBER'),
          value: ACCESS_LEVELS.PROJECT_MEMBER,
        },
        noAccess: {
          name: T.translate('NO_ACCESS'),
          value: ACCESS_LEVELS.NO_ACCESS,
        },
      }),
      [T]
    );

    const STATUS = useMemo(
      () => ({
        active: {
          name: T.translate('STATUS_ACTIVE'),
          value: MEMBER_STATUS.ACTIVE,
        },
        invited: {
          name: T.translate('STATUS_INVITED'),
          value: MEMBER_STATUS.PENDING,
        },
        notInvited: {
          name: T.translate('STATUS_NOT_INVITED'),
          value: MEMBER_STATUS.NOT_INVITED,
        },
        disabled: {
          name: T.translate('STATUS_DISABLED'),
          value: MEMBER_STATUS.DISABLED,
        },
      }),
      [T]
    );

    const {
      accountCompanies,
      onChangeCompanyInput,
      isFetchingAccountCompanies,
    } = useGetAccountCompanies();

    const latestOnChangeCompanyInput = useRef(onChangeCompanyInput);

    useEffect(() => {
      if (!latestOnChangeCompanyInput.current) return;
      latestOnChangeCompanyInput.current = onChangeCompanyInput;
    }, [onChangeCompanyInput]);

    useEffect(() => {
      const handler = setTimeout(() => {
        latestOnChangeCompanyInput.current(
          state.accountMemberFilter.companySearchInput.trim()
        );
      }, 800);

      return () => clearTimeout(handler);
    }, [state.accountMemberFilter.companySearchInput]);

    useEffect(() => {
      const availableAccessLevels = [
        ACCESSES.accountAdmin,
        ACCESSES.executive,
        ACCESSES.projectAdmin,
        ACCESSES.projectMember,
        ACCESSES.noAccess,
      ];

      setAccessLevels(availableAccessLevels);
    }, [ACCESSES]);

    useEffect(() => {
      const availableStatus = [
        STATUS.active,
        STATUS.invited,
        STATUS.notInvited,
        STATUS.disabled,
      ];

      setStatus(availableStatus);
    }, [STATUS]);

    const { activeAccountRoles, isFetchingAccountRoles, onChangeRoleInput } =
      useGetAccountRoles();

    const latestOnChangeRoleInput = useRef(onChangeRoleInput);

    useEffect(() => {
      if (!latestOnChangeRoleInput.current) return;
      latestOnChangeRoleInput.current = onChangeRoleInput;
    }, [onChangeRoleInput]);

    useEffect(() => {
      const handler = setTimeout(() => {
        latestOnChangeRoleInput.current(
          state.accountMemberFilter.roleSearchInput.trim()
        );
      }, 800);

      return () => clearTimeout(handler);
    }, [state.accountMemberFilter.roleSearchInput]);

    const handleCompanyInputChange = useCallback((value) => {
      dispatch({
        type: ACTIONS.SET_COMPANY_SEARCH_INPUT,
        payload: { value },
      });
    }, []);

    const companyChange = useCallback(
      ({ value = null, trackAnalytics = true }) => {
        if (trackAnalytics) {
          const action = value === null ? 'clear' : 'set';
          sendAnalytics(TRACK_EVENTS.NAME.COMPANY, { action });
        }

        dispatch({
          type: ACTIONS.SET_COMPANY,
          payload: { value, save: true },
        });
      },
      [sendAnalytics]
    );

    const handleCompanyChange = useCallback(
      (value) => companyChange({ value }),
      [companyChange]
    );

    const handleRoleInputChange = useCallback((value) => {
      dispatch({
        type: ACTIONS.SET_ROLE_SEARCH_INPUT,
        payload: { value },
      });
    }, []);

    const roleChange = useCallback(
      ({ value = null, trackAnalytics = true }) => {
        if (trackAnalytics) {
          const action = value === null ? 'clear' : 'set';
          sendAnalytics(TRACK_EVENTS.NAME.ROLE, { action });
        }

        dispatch({
          type: ACTIONS.SET_ROLE,
          payload: { value, save: true },
        });
      },
      [sendAnalytics]
    );

    const handleRoleChange = useCallback(
      (value) => roleChange({ value }),
      [roleChange]
    );

    const handleAccessLevelInputChange = useCallback((value) => {
      dispatch({
        type: ACTIONS.SET_ACCESS_LEVEL_SEARCH_INPUT,
        payload: { value },
      });
    }, []);

    const accessLevelChange = useCallback(
      ({ value = null, trackAnalytics = true }) => {
        if (trackAnalytics) {
          const action = value === null ? 'clear' : 'set';
          sendAnalytics(TRACK_EVENTS.NAME.ACCESS_LEVEL, { action });
        }

        dispatch({
          type: ACTIONS.SET_ACCESS_LEVEL,
          payload: { value, save: true },
        });
      },
      [sendAnalytics]
    );

    const handleAccessLevelChange = useCallback(
      (value) => accessLevelChange({ value }),
      [accessLevelChange]
    );

    const handleStatusInputChange = useCallback((value) => {
      dispatch({
        type: ACTIONS.SET_STATUS_SEARCH_INPUT,
        payload: { value },
      });
    }, []);

    const statusChange = useCallback(
      ({ value = null, trackAnalytics = true }) => {
        if (trackAnalytics) {
          const action = value === null ? 'clear' : 'set';
          sendAnalytics(TRACK_EVENTS.NAME.STATUS, { action });
        }

        dispatch({
          type: ACTIONS.SET_STATUS,
          payload: { value, save: true },
        });
      },
      [sendAnalytics]
    );

    const handleStatusChange = useCallback(
      (value) => statusChange({ value }),
      [statusChange]
    );

    useEffect(() => {
      const filters = [
        {
          name: 'status',
          value: state.accountMemberFilter.selectedStatus?.value || null,
        },
        {
          name: 'defaultCompanyName',
          value: state.accountMemberFilter.selectedCompany?.name || null,
        },
        {
          name: 'defaultRoleName',
          value: state.accountMemberFilter.selectedRole?.name || null,
        },
        {
          name: 'accessLevels',
          value: state.accountMemberFilter.selectedAccessLevel?.value || null,
        },
      ];
      onFilterUpdate(filters);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const handleClearFilters = () => {
      statusChange({ trackAnalytics: false });
      companyChange({ trackAnalytics: false });
      roleChange({ trackAnalytics: false });
      accessLevelChange({ trackAnalytics: false });
      sendAnalytics(TRACK_EVENTS.NAME.CLEAR);
    };

    useImperativeHandle(ref, () => ({ clearFilters: handleClearFilters }));

    return (
      <AccountMemberFilterPanel
        accessLevelSearchInput={
          state.accountMemberFilter.accessLevelSearchInput
        }
        accessLevels={accessLevels}
        companies={accountCompanies}
        companySearchInput={state.accountMemberFilter.companySearchInput}
        isFetchingCompanies={isFetchingAccountCompanies}
        isFetchingRoles={isFetchingAccountRoles}
        onAccessLevelChange={handleAccessLevelChange}
        onChangeAccessLevelInput={handleAccessLevelInputChange}
        onChangeCompanyInput={handleCompanyInputChange}
        onChangeRoleInput={handleRoleInputChange}
        onChangeStatusInput={handleStatusInputChange}
        onClose={handleClose}
        onCompanyChange={handleCompanyChange}
        onRoleChange={handleRoleChange}
        onStatusChange={handleStatusChange}
        open={open}
        roleSearchInput={state.accountMemberFilter.roleSearchInput}
        roles={activeAccountRoles}
        selectedAccessLevel={state.accountMemberFilter.selectedAccessLevel}
        selectedCompany={state.accountMemberFilter.selectedCompany}
        selectedRole={state.accountMemberFilter.selectedRole}
        selectedStatus={state.accountMemberFilter.selectedStatus}
        status={status}
        statusSearchInput={state.accountMemberFilter.statusSearchInput}
      />
    );
  }
);

AccountMemberFilterPanelContainer.displayName = 'AccountMemberFilter';

AccountMemberFilterPanelContainer.opStatuses = OP_STATUSES;

AccountMemberFilterPanelContainer.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
  onFilterUpdate: PropTypes.func,
};

AccountMemberFilterPanelContainer.defaultProps = {
  onClose: () => {},
  open: false,
  onFilterUpdate: () => {},
};

export default AccountMemberFilterPanelContainer;
